import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  Star,
  Target,
  BarChart3,
  Building2,
  Utensils,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const SQF_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Utensils size={18} className="text-indigo-400" />
            <span>Food Safety & Quality Certification</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
            SQF Certification
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Safe Quality Food - Comprehensive Food Safety and Quality Management
            for Global Supply Chains
          </p>
        </motion.div>
      </section>

      {/* What is SQF */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is SQF Certification?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              SQF certification confirms that an organization has implemented an
              effective Food Safety and Quality Management System (FSQMS),
              aligned with SQF standards, to ensure the production, handling,
              and distribution of food products that meet the highest standards
              of safety and quality.
            </p>
            <p>
              The Safe Quality Food (SQF) program is an internationally
              recognized food safety management certification program designed
              to ensure the production of safe, high-quality food products
              across the global supply chain. SQF is often a requirement for
              doing business with major food companies and retailers worldwide.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of SQF Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Comprehensive Food Safety Assurance",
                desc: "Ensures food safety at every step from raw material sourcing to final consumer products.",
                icon: ShieldCheck,
              },
              {
                title: "HACCP-Based Risk Management",
                desc: "Implements Hazard Analysis and Critical Control Points to manage food safety hazards effectively.",
                icon: Target,
              },
              {
                title: "Quality Assurance",
                desc: "Ensures food products meet high-quality standards from production to packaging and distribution.",
                icon: CheckCircle,
              },
              {
                title: "Regulatory Compliance",
                desc: "Aligns with local, national, and international food safety regulations.",
                icon: FileCheck,
              },
              {
                title: "Continuous Improvement",
                desc: "Encourages ongoing monitoring, review, and enhancement of food safety systems.",
                icon: TrendingUp,
              },
              {
                title: "Traceability & Transparency",
                desc: "Ensures products can be traced from source to final consumer.",
                icon: Globe,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-indigo-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* Food Safety & Quality Management */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is Food Safety and Quality Management?
          </h2>
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              Food Safety and Quality Management (FSQMS) is a comprehensive
              system that ensures food safety and quality are embedded in every
              aspect of the food production and supply process:
            </p>
            <ul className="space-y-3">
              {[
                "Risk Assessment and Control - Identifying and assessing food safety risks in raw materials, processes, and finished products",
                "GMP and GAP - Implementing foundational quality practices for cleanliness, sanitation, and safe handling",
                "HACCP and CCPs - Applying HACCP principles to manage critical control points in production",
                "Traceability and Documentation - Ensuring products can be traced from source to final consumer",
                "Employee Training - Ensuring employees are trained in food safety and quality practices",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-indigo-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Importance of SQF Certification for Businesses
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Ensures compliance with local, national, and international food safety regulations",
              "Facilitates market access to retailers and food service providers requiring SQF",
              "Increases consumer confidence in food safety and product quality",
              "Improves operational processes and reduces waste",
              "Provides global recognition and competitive advantage",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-purple-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Significance */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Significance of SQF Certification
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            SQF certification helps organizations:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              "Demonstrate commitment to food safety and quality",
              "Ensure consistent product safety and quality",
              "Meet regulatory and customer requirements",
              "Increase market competitiveness",
              "Enhance brand reputation",
              "Achieve global market access",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-indigo-100 to-purple-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-indigo-600 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of SQF Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Improved Food Safety reducing risks and preventing contamination",
              "Increased Customer Trust demonstrating quality commitment",
              "Regulatory Compliance meeting global food safety standards",
              "Operational Efficiency optimizing processes and reducing waste",
              "Access to New Markets facilitating entry to global supply chains",
              "Enhanced Brand Reputation strengthening market position",
              "Reduced Product Recalls minimizing liability and costs",
              "Competitive Advantage standing out in the marketplace",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-indigo-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries That Require SQF Certification
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            SQF certification is essential for food businesses across multiple
            sectors:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Food Manufacturing",
              "Food Processing",
              "Packaged Foods",
              "Meat, Poultry & Seafood Processing",
              "Dairy Processing",
              "Beverage Production",
              "Food Storage & Distribution",
              "Food Retail & Service",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg"
              >
                <Factory className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Get Certification */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Get SQF Certification
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Gap Analysis",
                desc: "Evaluate current food safety and quality practices against SQF standards",
              },
              {
                step: 2,
                title: "FSMS Documentation",
                desc: "Develop and implement policies, procedures, and records for food safety",
              },
              {
                step: 3,
                title: "Employee Training",
                desc: "Train employees on food safety, quality management, and compliance",
              },
              {
                step: 4,
                title: "Implementation",
                desc: "Put in place effective controls, monitoring systems, and corrective actions",
              },
              {
                step: 5,
                title: "Internal Audits",
                desc: "Conduct internal audits and management reviews for effectiveness",
              },
              {
                step: 6,
                title: "Certification Audit",
                desc: "Undergo audit by accredited certification body to verify compliance",
              },
              {
                step: 7,
                title: "Continual Improvement",
                desc: "Continuously monitor and improve food safety and quality systems",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for SQF Certification?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions Pvt. Ltd. provides expert SQF consultancy services,
              assisting food businesses in implementing and maintaining a
              comprehensive Food Safety and Quality Management System.
            </p>
            <p className="text-lg text-gray-700 mb-6">Our services include:</p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "FSMS and HACCP Plan Development",
                "Supplier Control and Traceability",
                "Employee Training on Food Safety",
                "Internal Audits and Pre-Certification",
                "Certification Body Liaison and Support",
                "Continuous Improvement Guidance",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-indigo-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help food businesses achieve safe, compliant, and high-quality
              food production through practical SQF implementation.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-indigo-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-indigo-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default SQF_Details;
